import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { XIcon } from '@heroicons/react/solid'

// @mentions for DMs, after Closing Credits' course chat: an inline list that
// opens as you type "@", and an @ button that opens a searchable dialog for
// anyone who doesn't know the trick.
//
// Mentions are plain `@username` text, the same thing the server scans for
// (PostMessage::MENTION_TOKEN), so a mention typed on the phone app works too.
// Only thread members are offered: someone outside it couldn't read the message.

// Keep in step with PostMessage::MENTION_TOKEN.
export const MENTION_TOKEN = /(^|[^\w@])@([a-z0-9_-]{1,40})(?![\w-])/gi
// The token being typed right before the caret, if any.
export const ACTIVE_MENTION = /(?:^|[^\w@])@([a-z0-9_-]{0,40})$/i
export const EVERYONE = 'everyone'

const INLINE_MAX = 6

const everyoneItem = (count) => ({
  key: EVERYONE,
  username: EVERYONE,
  displayName: 'Everyone',
  subtitle: `Notify all ${count} people in this thread`,
  group: true
})

// people: post_users rows. me / ownerId decide who is offered and whether
// @everyone is (only the thread's owner may ping the whole thread).
export const mentionCandidates = (people, me, ownerId) => {
  const members = (people || [])
    .filter(p => p.userId !== me && p.userUsername)
    .map(p => ({
      key: p.userUsername,
      username: p.userUsername,
      displayName: p.userDisplayName,
      imageUrl: p.publicImageUrl
    }))
  const groups = me && me === ownerId && members.length > 1 ? [everyoneItem(members.length)] : []
  return { groups, members }
}

const matches = (query) => (item) => {
  const q = query.toLowerCase()
  return item.username.toLowerCase().startsWith(q) || (item.displayName || '').toLowerCase().includes(q)
}

const Avatar = ({ item }) => (
  item.group
    ? <span className='h-9 w-9 shrink-0 rounded-full bg-cccblue/15 text-cccblue flex items-center justify-center font-bold'>@</span>
    : item.imageUrl
      ? <img src={item.imageUrl} alt='' className='h-9 w-9 shrink-0 rounded-full bg-gray-200 object-cover' />
      : <span className='h-9 w-9 shrink-0 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 flex items-center justify-center font-semibold'>{(item.displayName || item.username)[0]?.toUpperCase()}</span>
)

// One row, shared by the inline list and the dialog so the two can't drift.
const MentionRow = ({ item, active, onPick, onHover }) => (
  <button
    type='button'
    // mousedown, not click: a click would blur the editor first and lose the caret.
    onMouseDown={(e) => { e.preventDefault(); onPick(item) }}
    onMouseEnter={onHover}
    className={`w-full flex items-center gap-3 px-3 py-2 text-left ${active ? 'bg-cccblue/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/60'}`}
  >
    <Avatar item={item} />
    <span className='min-w-0 flex-1'>
      <span className='block truncate text-sm font-medium text-gray-900 dark:text-white'>{item.displayName || item.username}</span>
      {item.subtitle && <span className='block truncate text-xs text-gray-500 dark:text-gray-400'>{item.subtitle}</span>}
    </span>
    {!item.group && <span className='shrink-0 text-xs text-gray-400'>{item.username}</span>}
  </button>
)

const SectionTitle = ({ children }) => (
  <div className='px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400'>{children}</div>
)

// The inline list. Keyboard handling lives with the composer (it owns the
// editor's keydown), so this only renders the flat `items` it is given.
export const MentionSuggestions = ({ query, groups, people, activeIndex, onPick, onHover }) => {
  if (groups.length + people.length === 0) return null
  let index = -1
  const row = (item) => {
    index += 1
    const i = index
    return <MentionRow key={item.key} item={item} active={i === activeIndex} onPick={onPick} onHover={() => onHover(i)} />
  }

  return <div className='absolute left-0 right-0 bottom-full mb-1 max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg z-30'>
    <div className='sticky top-0 px-3 py-2 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400'>
      {query ? <>Members matching <span className='text-cccblue'>@{query}</span></> : 'Members'}
    </div>
    {groups.length > 0 && <><SectionTitle>Groups</SectionTitle>{groups.map(row)}</>}
    {people.length > 0 && <><SectionTitle>People</SectionTitle>{people.map(row)}</>}
  </div>
}

// Flat, capped list for the inline picker, in the order it renders.
export const inlineItems = (query, candidates) => {
  const groups = candidates.groups.filter(matches(query))
  const people = candidates.members.filter(matches(query)).slice(0, INLINE_MAX)
  return { groups, people, flat: [...groups, ...people] }
}

export const MentionModal = ({ open, onClose, candidates, onPick }) => {
  const [query, setQuery] = useState('')
  const input = useRef(null)

  useEffect(() => {
    if (!open) { setQuery(''); return undefined }
    input.current?.focus({ preventScroll: true })
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const groups = useMemo(() => candidates.groups.filter(matches(query)), [candidates, query])
  const people = useMemo(() => candidates.members.filter(matches(query)), [candidates, query])

  if (!open) return null

  return <div className='fixed inset-0 z-50 flex items-end sm:items-center justify-center' role='dialog' aria-modal='true'>
    <div className='absolute inset-0 bg-black/40' onClick={onClose} />
    <div className='relative w-full sm:max-w-lg max-h-[80vh] flex flex-col rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 shadow-xl'>
      <div className='flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700'>
        <h2 className='text-base font-semibold text-gray-900 dark:text-white'>Mention someone</h2>
        <button type='button' onClick={onClose} aria-label='Close' className='text-gray-400 hover:text-gray-700 dark:hover:text-white'>
          <XIcon className='h-5 w-5' />
        </button>
      </div>
      <div className='p-4 border-b border-gray-100 dark:border-gray-700'>
        <input
          ref={input}
          value={query}
          onChange={(e) => setQuery(e.target.value.replace(/^@/, ''))}
          placeholder={`Search ${candidates.members.length} ${candidates.members.length === 1 ? 'person' : 'people'}...`}
          className='w-full rounded-lg border-2 border-cccblue/60 focus:border-cccblue focus:ring-0 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white px-3 py-2'
        />
      </div>
      <div className='flex-1 min-h-0 overflow-y-auto py-1 pb-[max(0.5rem,env(safe-area-inset-bottom))]'>
        {groups.length > 0 && <><SectionTitle>Groups {groups.length}</SectionTitle>{groups.map(item => <MentionRow key={item.key} item={item} onPick={onPick} onHover={() => {}} />)}</>}
        {people.length > 0 && <><SectionTitle>People {people.length}</SectionTitle>{people.map(item => <MentionRow key={item.key} item={item} onPick={onPick} onHover={() => {}} />)}</>}
        {groups.length + people.length === 0 && <p className='px-5 py-6 text-sm text-center text-gray-500'>Nobody in here answers to that. Try another name.</p>}
      </div>
    </div>
  </div>
}

// Paint `@username` pills into a message's HTML. Only tokens that resolve to
// someone in the thread (or a real @everyone) are painted, so a pill means
// somebody actually got pinged. Tags are left alone; only text between them
// is touched, and only ever wrapped around [a-z0-9_-], so nothing new can
// sneak into the markup.
export const paintMentions = (html, knownUsernames) => {
  if (!html || html.indexOf('@') === -1) return html
  return html.split(/(<[^>]+>)/).map(part => (
    part.startsWith('<')
      ? part
      : part.replace(MENTION_TOKEN, (whole, lead, name) => (
        knownUsernames.has(name.toLowerCase())
          ? `${lead}<span class="rounded px-0.5 bg-cccblue/15 text-cccblue font-medium">@${name}</span>`
          : whole
      ))
  )).join('')
}

// Lowercased tokens in a message, read from its text, not its markup.
export const mentionTokensIn = (html) => {
  const text = (html || '').replace(/<[^>]+>/g, ' ')
  return Array.from(text.matchAll(MENTION_TOKEN), m => m[2].toLowerCase())
}

Avatar.propTypes = { item: PropTypes.object.isRequired }
MentionRow.propTypes = { item: PropTypes.object.isRequired, active: PropTypes.bool, onPick: PropTypes.func.isRequired, onHover: PropTypes.func }
SectionTitle.propTypes = { children: PropTypes.node }
MentionSuggestions.propTypes = {
  query: PropTypes.string,
  groups: PropTypes.array.isRequired,
  people: PropTypes.array.isRequired,
  activeIndex: PropTypes.number,
  onPick: PropTypes.func.isRequired,
  onHover: PropTypes.func.isRequired
}
MentionModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  candidates: PropTypes.object.isRequired,
  onPick: PropTypes.func.isRequired
}
